import { createSlice } from '@reduxjs/toolkit'

import { jwtDecode } from 'jwt-decode'

import config, { vars } from '@app/config'
import { Jwt } from '@app/models'
import { clearCookies, logout, setCookie } from '@app/services'

import { getAuthMode, sendEventAuth } from '@app/utils'
import { removeURLTokenHashFromQuery } from '@app/utils/url/getURLTokenHashFromQuery'

import { hasFeature, WebViewFeature } from '@app/utils/webview/hasFeature'

import { initialState } from './constants'
import { refreshTokenThunk, getURLTokenByHashThunk } from './thunks'

const tokenRoles = (access_token: string) =>
  (jwtDecode(access_token) as Jwt)?.roles || []

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setURLToken: (state, action) => {
      const { urlToken } = action.payload || {}
      setCookie(vars.url_token, urlToken, 48)
      state.tokenData.urlToken = urlToken
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshTokenThunk.fulfilled, (state, action) => {
      const { isBackOfficeTokenMode } = getAuthMode()

      state.tokenData.token = isBackOfficeTokenMode
        ? action.payload?.access_token
        : action.payload?.token
      state.tokenData.refresh_token = isBackOfficeTokenMode
        ? action.payload?.refresh_token
        : action.payload?.refreshToken

      const hasDelegatedJwtExchange =
        config.identity.isWebView &&
        hasFeature(WebViewFeature.DelegatedJwtExchange)
      // Do not force logout in WebView if refresh_token is absent
      if (!state.tokenData.refresh_token && !hasDelegatedJwtExchange) {
        logout()
        return
      }
      if (config.identity.isWebView && hasDelegatedJwtExchange) {
        sendEventAuth(state.tokenData.token, state.tokenData.refresh_token)
      }
    })
    builder.addCase(refreshTokenThunk.rejected, () => {
      console.log('Cannot refresh user token')
      logout()
    })
    builder.addCase(getURLTokenByHashThunk.fulfilled, (state, action) => {
      const { access_token, urlToken } = action.payload || {}
      clearCookies()

      state.tokenData.refresh_token = null

      setCookie(
        config.identity.isBackOffice
          ? vars.b2b_access_token
          : vars.b2c_access_token,
        access_token,
        1
      )
      state.tokenData.token = access_token

      setCookie(vars.url_token, urlToken, 48)
      state.tokenData.urlToken = urlToken

      setCookie(vars.roles, encodeURIComponent(tokenRoles(access_token)), 48)
    })
    builder.addCase(getURLTokenByHashThunk.rejected, () => {
      console.log('Cannot get URL token')
      removeURLTokenHashFromQuery()
    })
  },
})

export const { setURLToken } = authSlice.actions
export default authSlice.reducer
